import { Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useAppUpdatePrompt } from '@hooks/use-app-update';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/lumen/dialog';
import './app-update.css';

/** Above the notification stack (10001); app-update.css lifts the scrim to the same tier. */
const APP_UPDATE_MODAL_Z_INDEX = 10002;

// The modal can't be dismissed: the only way out is the reload.
const keepOpen = (event: Event) => event.preventDefault();

/**
 * Asks the tab to reload once a newer build is deployed. A plain reload is
 * enough to pick it up: index.html is served no-store and every asset name is
 * content-hashed, so the reloaded page references the new build's files.
 * Mounted outside the Router: no Link or useNavigate in here.
 */
export default function AppUpdateModal() {
  const opened = useAppUpdatePrompt();
  const { t } = useLanguage();
  const [reloading, setReloading] = useState(false);

  const reload = () => {
    setReloading(true);
    window.location.reload();
  };

  return (
    // Controlled by the hook only: no Radix dismissal path reaches onOpenChange.
    <Dialog open={opened}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={keepOpen}
        onPointerDownOutside={keepOpen}
        onInteractOutside={keepOpen}
        data-app-update-modal=""
        style={{ zIndex: APP_UPDATE_MODAL_Z_INDEX }}
        className="sm:max-w-sm"
      >
        <div className="flex flex-col items-center gap-3 py-4">
          <span aria-hidden className="grid size-14 place-items-center rounded-full bg-label/10 text-info-ink">
            <RefreshCw className="size-7" />
          </span>
          <DialogTitle className="text-center font-bold text-lg">{t('appUpdate.title')}</DialogTitle>
          <DialogDescription className="text-center text-fg-muted text-sm">{t('appUpdate.message')}</DialogDescription>
          <Button type="button" className="mt-3 w-full" disabled={reloading} onClick={reload}>
            {reloading ? (
              <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
            ) : (
              <RefreshCw aria-hidden />
            )}
            {t('appUpdate.reload')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
